import dayjs from 'dayjs';
import { z } from 'zod';

import { Periodicity } from '@/pages/strategies/types/Contribution.types';
import { DropdownOption } from '@/types/generalTypes';

export type StraSummaryItem = {
  title: string;
  value: string;
};

export interface IUpdateUserOnboardingFlagsDto {
  showStrategyOnboardingVideo?: boolean;
}

export type TNewStrategyProgressStepStats = 'disabled' | 'active' | 'completed';

export type TStepStateStyles = Record<
  TNewStrategyProgressStepStats,
  {
    circleBorder: string;
    circleBg: string;
    titleColor: string;
    subtextColor: string;
    rowBg: string;
  }
>;

export type TCreationFlow = 'Manual' | 'UploadFile';

export type StrategyDashboardDTO = {
  id: string;
  name: string;
  slug: string;
  description?: string;
  coverImageId?: string;
  coverImageUrl?: string;
  startDate: Date;
  endDate: Date;
  statusId: string;
  strategyCategory?: DropdownOption;
  strategyOwners?: DropdownOption[];
  primaryFocuses: DropdownOption[];
  deliverableStatuses: DropdownOption[];
  allResponses: number;
  completedResponses: number;
};

export type StrategyControlPanelStatus = {
  statusId: string;
  title: string;
  count: number;
};

type StrategyControlPanelHorizon = {
  fromDay: number;
  toDay: number;
  fromDate: string;
  toDate: string;
  deliverableCount: number;
  signalUpdateCount: number;
  nextDueDate?: string | null;
};

export type StrategyControlPanelDueBucket = {
  key: string;
  category: 'Overdue' | 'Current' | 'Upcoming';
  label: string;
  fromDate?: string | null;
  toDate?: string | null;
  deliverableCount: number;
  closedCount: number;
  overdueCount: number;
  currentCount: number;
  nearFutureCount: number;
  laterCount: number;
  deliverableIds: string[];
};

export type StrategyControlPanelItem = {
  category: 'Overdue' | 'Configuration' | 'Upcoming';
  issueType: string;
  title: string;
  detail: string;
  dueDate?: string | null;
  strategyId: string;
  strategyName: string;
  strategySlug: string;
  deliverableId?: string | null;
  actionId?: string | null;
  deliverableOrder?: string | null;
  signalId?: string | null;
  responseId?: string | null;
  userId?: string | null;
  occurrenceCount: number;
};

export type StrategyControlPanelRule = {
  category: 'Overdue' | 'Configuration';
  issueType: string;
  count: number;
  deliverableIds: string[];
  actionIds: string[];
};

export type StrategyControlPanelAssignee = {
  key: string;
  id: string;
  type: 'Member' | 'Business Unit';
  name: string;
  strategyIds: string[];
  deliverableIds: string[];
};

export type StrategySignalActivity = {
  key: string;
  fromDate: string;
  toDate: string;
  requestedUpdateCount: number;
  previousRequestedUpdateCount: number;
  unpromptedUpdateCount: number;
  previousUnpromptedUpdateCount: number;
};

export type StrategyControlPanelDTO = {
  asOfDate: string;
  activeStrategyCount: number;
  totalStrategyCount: number;
  overdueDeliverableCount: number;
  overdueSignalUpdateCount: number;
  configurationIssueCount: number;
  totalDeliverableCount: number;
  startedDeliverableCount: number;
  deliverableStatuses: StrategyControlPanelStatus[];
  upcoming: StrategyControlPanelHorizon[];
  attentionItems: StrategyControlPanelItem[];
  attentionItemsTruncated: boolean;
  assignees: StrategyControlPanelAssignee[];
  strategies: StrategyControlPanelStrategy[];
};

export type StrategyControlPanelStrategy = {
  strategyId: string;
  strategyName: string;
  strategySlug: string;
  isActive: boolean;
  overdueDeliverableCount: number;
  overdueSignalUpdateCount: number;
  overdueSignalCount: number;
  dueNowSignalCount: number;
  upToDateSignalCount: number;
  overdueSignalDeliverableIds: string[];
  dueNowSignalDeliverableIds: string[];
  upToDateSignalDeliverableIds: string[];
  configurationIssueCount: number;
  totalDeliverableCount: number;
  startedDeliverableCount: number;
  overdueAttentionDeliverableIds: string[];
  configurationDeliverableIds: string[];
  deliverableStatuses: StrategyControlPanelStatus[];
  dueBuckets: StrategyControlPanelDueBucket[];
  attentionRules: StrategyControlPanelRule[];
  attentionItems: StrategyControlPanelItem[];
  attentionItemsTruncated: boolean;
  signalActivityPeriods: StrategySignalActivity[];
};

export const straViewSearchSchema = z.object({
  attention: z
    .enum(['overdue', 'configuration', 'due', 'signal-overdue', 'signal-due', 'signal-up-to-date'])
    .optional(),
  deliverableId: z.string().optional(),
  issueType: z.string().optional(),
  dueFrom: z.string().optional(),
  dueTo: z.string().optional(),
  assignees: z.string().optional()
});

export type StraViewSearch = z.infer<typeof straViewSearchSchema>;

export type CadenceRightClickTarget = number | null;

export type TimeSlot = {
  selectedDate: string;
  memberIds: string[];
};

export type AddEngagementContributionRequest = {
  order: string;
  name: string;
  responseType: string;
  responseOptions: { id: string; title: string }[];
  reportTypeIds: string[];
  subIndicator: string | null;
  timeSlots: TimeSlot[];
};

export type UpdateEngagementContributionRequest = AddEngagementContributionRequest & {
  contributionId: string;
  hasLegacyResponses?: boolean;
  userConfirmed?: boolean;
};

export type TSelectionSnapshot = {
  selectedTimeSlotIndexes: number[];
  timeSlots: TimeSlot[];
};

export interface IBulkUpdatePayload {
  actionIds?: string[];
  deliverableIds?: string[];
  assignBusinessUnitIds?: string[];
  unassignBusinessUnitIds?: string[];
  contributionIds?: string[];
  periodicity?: Periodicity;
  fromDate?: string;
  toDate?: string;
  overrideBusinessUnitIds?: string[];
  reportTypeIds?: string[];
  toDelete?: boolean;
  statusId?: string;
  actionAreaId?: string;
}

export type BulkUpdateResponse = {
  requireConfirmation: boolean;
  message: string;
};

export type EngagementContributionCreateResponse = {
  result?: string;
  isSuccess: boolean;
  isNotFound: boolean;
  isForbidden: boolean;
  requireConfirmation: boolean;
  message?: string;
  errorMessages: { key: string; value: string }[];
};

export interface DeliverableWithLastContribution {
  id: string;
  name: string;
  actionOrder?: string;
  order: string;
  lastContributionOrder: string;
}

export type SignalResponseOption = {
  id: string;
  title: string;
  inputValue?: string;
};

export const responseTypesWithOptions = [
  'SingleChoice',
  'MultipleChoice',
  'MultipleNumberResponses',
  'MultipleTextResponses'
] as const;

export interface IBulkCreateContributionPayload {
  deliverables: DeliverableWithLastContribution[];
  contribution: any;
}

export const straFormBaseSchema = z.object({
  startDate: z.date(),
  endDate: z.date(),
  name: z.string().min(1, 'Strategy name is required'),
  statusId: z.string(),
  parentStrategyId: z.string().uuid().optional(),
  iconName: z.string().optional(),
  iconColor: z.string().optional(),
  strategyOwnerIds: z.array(z.string().uuid()).min(1, 'Please select at least one owner')
});

export const straFormSchema = straFormBaseSchema.refine(
  (data) => !dayjs(data.startDate).isAfter(data.endDate, 'month'),
  {
    message: 'To date cannot be earlier than from date',
    path: ['startDate']
  }
);

export const upsertSignalFormSchema = z
  .object({
    order: z.string().min(1, 'Order is required'),
    name: z.string().min(1, 'Question is required'),
    responseType: z.string().min(1, 'Response Type is required'),
    responseOptions: z.array(
      z.object({
        id: z.string(),
        title: z.string(),
        inputValue: z.string().optional()
      })
    ),
    reportTypeIds: z.array(z.string()),
    subIndicator: z.string().optional()
  })
  .superRefine((data, ctx) => {
    const requiresOptions = responseTypesWithOptions.includes(
      data.responseType as (typeof responseTypesWithOptions)[number]
    );
    if (requiresOptions && data.responseOptions.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Response option is required.',
        path: ['responseOptions']
      });
    }
  });

export interface IContributionInfoDTO {
  label: string;
  isSocialFeedEnabled: boolean;
  responses: {
    responseID: string;
    bupID?: string;
    completed: boolean;
    isExpired: boolean;
    bupStatus: 'Approved' | 'InProgress' | 'ActionRequired' | 'ApprovalRequested';
    period: string;
    signalOrder?: string;
    responder?: {
      id: string;
      fullName: string;
    };
  }[];
}
